import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@/store/useStore';
import { initialsOf, hashHue } from '@/utils/colors';
import { changePassword } from '@/auth/authClient';
import { setCoreUnlock } from '@/data/opsClient';
import { Mail, Phone, Building2, Shield, KeyRound, LogOut, Sun, Moon, Camera, Trash2, Loader2, Pencil, Check, X, Send, AtSign, Copy, Unlock, Lock, AlertTriangle, Cake } from 'lucide-react';

/** Профиль пользователя — реальные данные вошедшего, смена пароля, выход. */
export function ProfilePage() {
  const { employees, branches, settings, patchSettings, toast } = useStore();
  const currentUser = useStore((s) => s.currentUser);
  const logout = useStore((s) => s.logout);
  const updateMyProfile = useStore((s) => s.updateMyProfile);
  const updateMyAvatar = useStore((s) => s.updateMyAvatar);
  const updateMyBirthDate = useStore((s) => s.updateMyBirthDate);
  const requestTelegramLink = useStore((s) => s.requestTelegramLink);
  const unlinkTelegram = useStore((s) => s.unlinkTelegram);
  const refreshMe = useStore((s) => s.refreshMe);
  const [confirmExit, setConfirmExit] = useState(false); // двухшаговый выход — надёжно на телефоне/во встройке

  // Реальные данные: имя/роль/почта — от вошедшего пользователя; телефон/филиал — из
  // связанной карточки сотрудника (если есть).
  const emp = useMemo(
    () => (currentUser?.employee_id ? employees.find((e) => e.id === currentUser.employee_id) ?? null : null),
    [employees, currentUser?.employee_id],
  );
  const fullName = currentUser?.full_name ?? 'Пользователь';
  const roleName = currentUser?.access_role_name ?? currentUser?.worker_type_name ?? null;
  const email = currentUser?.email ?? emp?.email ?? null;
  const phone = emp?.phone ?? null;
  const birthDate = emp?.birth_date ?? null;
  const login = currentUser?.login ?? null; // логин входа (только у local-пользователей)
  const branchId = emp?.branch_id ?? currentUser?.city_ids?.[0] ?? null;
  const branch = branchId ? branches.find((b) => b.id === branchId) : null;
  const isLocal = currentUser?.kind === 'local';
  // Учётка супер-админа служебная и общая — свой пароль ей менять нельзя (защита от дурака).
  const isSuperAdmin = currentUser?.access_role === 'superadmin';

  const isDark = settings.theme === 'dark';
  const fileRef = useRef<HTMLInputElement>(null);
  // Фото привязано к аккаунту (хранится на сервере, приходит в currentUser.avatar) —
  // видно на любом устройстве. Локального хранилища больше не используем.
  const photo = currentUser?.avatar ?? null;
  const [photoBusy, setPhotoBusy] = useState(false);

  // Редактирование контактов
  const [editData, setEditData] = useState(false);
  const [emailVal, setEmailVal] = useState('');
  const [phoneVal, setPhoneVal] = useState('');
  const [loginVal, setLoginVal] = useState('');
  const [birthDateVal, setBirthDateVal] = useState('');
  const [savingData, setSavingData] = useState(false);
  const startEditData = () => { setEmailVal(email ?? ''); setPhoneVal(phone ?? ''); setLoginVal(login ?? ''); setBirthDateVal(birthDate ?? ''); setEditData(true); };
  const saveData = async () => {
    if (savingData) return;
    // Логин меняем только у входа по логину. Пустой логин запрещён — иначе не войти.
    let loginArg: string | undefined;
    if (isLocal) {
      const l = loginVal.trim();
      if (!l) { toast({ kind: 'error', text: 'Логин не может быть пустым' }); return; }
      if (l.length < 3) { toast({ kind: 'error', text: 'Логин — минимум 3 символа' }); return; }
      // Отправляем логин, только если он реально изменился (иначе лишняя проверка уникальности).
      if (l !== (login ?? '')) loginArg = l;
    }
    setSavingData(true);
    try {
      await updateMyProfile(emailVal.trim() || null, phoneVal.trim() || null, loginArg);
      // Дата рождения сохраняется отдельным вызовом (не часть updateMyProfile — у неё другая
      // «природа», чат 02.11.2026: заполняется один раз, отдельно гонять лишний запрос только
      // когда поле реально менялось).
      if (emp && birthDateVal !== (birthDate ?? '')) {
        await updateMyBirthDate(birthDateVal || null);
      }
      toast({ kind: 'success', text: 'Данные сохранены' });
      setEditData(false);
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось сохранить' });
    } finally {
      setSavingData(false);
    }
  };

  // Смена пароля
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Привязка Telegram. Статус — из учётки (currentUser.telegram_linked, ставится ботом
  // при «Старт»). Привязка на аккаунте, поэтому доступна любому вошедшему.
  const tgLinked = !!currentUser?.telegram_linked;
  const [tgBusy, setTgBusy] = useState(false);
  // Ожидание привязки: после открытия бота опрашиваем /me, пока не появится привязка.
  const [tgWaiting, setTgWaiting] = useState(false);
  // Код привязки (параметр start из deep_link) — на случай, если кнопка не открыла бота
  // (например, во встройке): можно отправить боту тем же кодом сообщением вручную.
  const [tgCode, setTgCode] = useState<string | null>(null);
  const linkTelegram = async () => {
    if (tgBusy) return;
    setTgBusy(true);
    try {
      const url = await requestTelegramLink();
      try { setTgCode(new URL(url).searchParams.get('start')); } catch { setTgCode(null); }
      window.open(url, '_blank', 'noopener,noreferrer');
      setTgWaiting(true); // запускаем авто-опрос статуса
      toast({ kind: 'info', text: 'Откройте бота и нажмите «Старт». Статус обновится сам.' });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось создать ссылку' });
    } finally {
      setTgBusy(false);
    }
  };
  const copyTgCode = async () => {
    if (!tgCode) return;
    try {
      await navigator.clipboard.writeText(tgCode);
      toast({ kind: 'success', text: 'Код скопирован' });
    } catch {
      toast({ kind: 'error', text: 'Не удалось скопировать' });
    }
  };

  // Авто-опрос статуса привязки: раз в 3с до ~2 минут, пока telegram_linked не станет true.
  // Останавливается при успехе, по лимиту попыток и при уходе со страницы (cleanup).
  useEffect(() => {
    if (!tgWaiting) return;
    if (tgLinked) { setTgWaiting(false); return; }
    let attempts = 0;
    const MAX = 40; // 40 × 3с ≈ 2 минуты
    const id = window.setInterval(() => {
      attempts += 1;
      if (attempts > MAX) { window.clearInterval(id); setTgWaiting(false); return; }
      void refreshMe();
    }, 3000);
    return () => window.clearInterval(id);
  }, [tgWaiting, tgLinked, refreshMe]);
  const unlinkTg = async () => {
    if (tgBusy) return;
    setTgBusy(true);
    try {
      await unlinkTelegram();
      toast({ kind: 'success', text: 'Telegram отвязан' });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось отвязать' });
    } finally {
      setTgBusy(false);
    }
  };
  const refreshTg = async () => {
    if (tgBusy) return;
    setTgBusy(true);
    try { await refreshMe(); } finally { setTgBusy(false); }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast({ kind: 'error', text: 'Нужен файл-изображение' }); return; }
    // Фото с телефона весит несколько МБ — уменьшаем до 256px (JPEG) и сохраняем в аккаунт.
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { toast({ kind: 'error', text: 'Не удалось обработать фото' }); return; }
        ctx.drawImage(img, 0, 0, w, h);
        const small = canvas.toDataURL('image/jpeg', 0.85);
        setPhotoBusy(true);
        updateMyAvatar(small)
          .then(() => toast({ kind: 'success', text: 'Фото профиля обновлено' }))
          .catch((err) => toast({ kind: 'error', text: err instanceof Error ? err.message : 'Не удалось сохранить фото' }))
          .finally(() => setPhotoBusy(false));
      };
      img.onerror = () => toast({ kind: 'error', text: 'Не удалось прочитать изображение' });
      img.src = r.result as string;
    };
    r.readAsDataURL(f);
  };
  const removePhoto = () => {
    setPhotoBusy(true);
    updateMyAvatar(null)
      .catch((err) => toast({ kind: 'error', text: err instanceof Error ? err.message : 'Не удалось удалить фото' }))
      .finally(() => setPhotoBusy(false));
  };

  // Рубильник «временно разрешить правку старых карточек всем» — только супер-админ, с
  // подтверждением (двухшаговая кнопка, как «Выйти» ниже). Флаг живёт на сервере
  // (settings id='ops'), сюда приходит через settings.core_unlock (см. GET /settings).
  const coreUnlock = settings.core_unlock;
  const coreUnlockOn = coreUnlock?.enabled === true;
  const [coreUnlockConfirm, setCoreUnlockConfirm] = useState(false);
  const [coreUnlockBusy, setCoreUnlockBusy] = useState(false);
  const toggleCoreUnlock = async (enabled: boolean) => {
    if (coreUnlockBusy) return;
    setCoreUnlockBusy(true);
    try {
      const result = await setCoreUnlock(enabled);
      useStore.setState((s) => ({ settings: { ...s.settings, core_unlock: result } }));
      toast({ kind: 'success', text: enabled ? 'Правка старых карточек разрешена всем' : 'Суточный замок снова работает как обычно' });
      setCoreUnlockConfirm(false);
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось переключить' });
    } finally {
      setCoreUnlockBusy(false);
    }
  };

  const submitPassword = async () => {
    if (pwBusy) return;
    if (newPw.length < 6) { toast({ kind: 'error', text: 'Новый пароль — минимум 6 символов' }); return; }
    if (newPw !== newPw2) { toast({ kind: 'error', text: 'Новые пароли не совпадают' }); return; }
    setPwBusy(true);
    try {
      await changePassword(curPw, newPw);
      toast({ kind: 'success', text: 'Пароль изменён' });
      setPwOpen(false); setCurPw(''); setNewPw(''); setNewPw2('');
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось сменить пароль' });
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-surface-0">
      <div className="max-w-[920px] mx-auto p-4 sm:p-5 space-y-5">
        <div className="panel p-5 sm:p-6 flex items-center gap-4 sm:gap-5">
          <div className="relative flex-shrink-0">
            {photo ? (
              <img src={photo} alt="" className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <span className="w-20 h-20 rounded-full grid place-items-center text-white font-bold text-2xl"
                style={{ backgroundColor: `hsl(${hashHue(fullName)}, 55%, 50%)` }}>
                {initialsOf(fullName)}
              </span>
            )}
            <button onClick={() => fileRef.current?.click()} title="Загрузить фото" disabled={photoBusy}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-accent text-white grid place-items-center shadow-soft hover:brightness-105 disabled:opacity-60">
              {photoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-1 truncate">{fullName}</h1>
            {roleName && <p className="text-sm text-ink-3 truncate">{roleName}</p>}
            <div className="flex items-center gap-3 mt-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full text-xs font-medium bg-status-paid/15 text-status-paid">
                <span className="w-1.5 h-1.5 rounded-full bg-status-paid" /> Активный
              </span>
              {photo && (
                <button onClick={removePhoto} className="text-xs text-ink-3 hover:text-status-refused inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Удалить фото
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="panel p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-ink-2">Данные</h2>
            {!editData ? (
              <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-accent" title={isLocal ? 'Редактировать логин, почту и телефон' : 'Редактировать почту и телефон'} onClick={startEditData}>
                <Pencil className="w-4 h-4" />
              </button>
            ) : (
              <div className="flex items-center gap-1">
                <button className="icon-btn !w-8 !h-8 text-status-paid" title="Сохранить" onClick={() => void saveData()} disabled={savingData}>
                  {savingData ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button className="icon-btn !w-8 !h-8 text-ink-3" title="Отмена" onClick={() => setEditData(false)} disabled={savingData}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {editData ? (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {/* Логин входа — только у local-пользователей (у Битрикс-входа логина нет). */}
              {isLocal && (
                <label className="block sm:col-span-2">
                  <span className="text-xs text-ink-3 mb-1 flex items-center gap-1.5"><AtSign className="w-3.5 h-3.5" /> Логин для входа</span>
                  <input className="input" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="логин" value={loginVal} onChange={(e) => setLoginVal(e.target.value)} disabled={savingData} />
                  <span className="text-[11px] text-ink-3 mt-1 block">После смены входите под новым логином (старый перестанет работать).</span>
                </label>
              )}
              <label className="block">
                <span className="text-xs text-ink-3 mb-1 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Электронная почта</span>
                <input className="input" type="email" autoComplete="email" placeholder="email@example.com" value={emailVal} onChange={(e) => setEmailVal(e.target.value)} disabled={savingData} />
              </label>
              <label className="block">
                <span className="text-xs text-ink-3 mb-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Телефон</span>
                <input className="input" type="tel" autoComplete="tel" placeholder="+7 ..." value={phoneVal} onChange={(e) => setPhoneVal(e.target.value)} disabled={savingData} />
              </label>
              {emp && (
                <label className="block">
                  <span className="text-xs text-ink-3 mb-1 flex items-center gap-1.5"><Cake className="w-3.5 h-3.5" /> Дата рождения</span>
                  <input className="input" type="date" value={birthDateVal} onChange={(e) => setBirthDateVal(e.target.value)} disabled={savingData} />
                </label>
              )}
            </div>
          ) : (
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              {isLocal && <InfoRow icon={AtSign} label="Логин для входа" value={login ?? undefined} />}
              <InfoRow icon={Mail} label="Электронная почта" value={email ?? undefined} />
              <InfoRow icon={Phone} label="Телефон" value={phone ?? undefined} />
              {emp && <InfoRow icon={Cake} label="Дата рождения" value={birthDate ? ddmmyyyy(birthDate) : undefined} />}
              <InfoRow icon={Shield} label="Роль" value={roleName ?? undefined} />
              <InfoRow icon={Building2} label="Филиал" value={branch?.name} />
            </dl>
          )}
        </div>

        {/* Telegram — привязка личных уведомлений. Доступна любому вошедшему (привязка на аккаунте). */}
        {currentUser && (
          <div className="panel p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-ink-2 flex items-center gap-2"><Send className="w-4 h-4 text-accent" /> Telegram</h2>
            </div>
            {tgLinked ? (
              <div className="space-y-3">
                {/* Явная зелёная плашка — сразу видно, что привязка прошла. */}
                <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 bg-status-paid/10 border border-status-paid/25">
                  <span className="w-7 h-7 rounded-full grid place-items-center bg-status-paid/20 text-status-paid flex-shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-status-paid">Telegram привязан</div>
                    <div className="text-[12px] text-ink-3">Личные уведомления бота приходят в ваш Telegram.</div>
                  </div>
                </div>
                <button className="btn-soft !justify-center text-status-refused" onClick={() => void unlinkTg()} disabled={tgBusy}>
                  {tgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Отвязать
                </button>
              </div>
            ) : tgWaiting ? (
              <div className="space-y-3">
                {/* Ожидание — пока пользователь жмёт «Старт» в боте, идёт авто-опрос. */}
                <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 bg-accent/8 border border-accent/25">
                  <Loader2 className="w-5 h-5 animate-spin text-accent flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink-1">Ожидание привязки…</div>
                    <div className="text-[12px] text-ink-3">Откройте бота и нажмите «Старт» — статус обновится сам.</div>
                  </div>
                </div>
                {tgCode && (
                  <div className="rounded-xl px-3.5 py-3 bg-surface-2 border border-surface-3 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-lg font-bold tracking-wider text-ink-1 tabular-nums select-all">{tgCode}</span>
                      <button className="icon-btn !w-8 !h-8 text-ink-3 hover:text-accent" title="Скопировать код" onClick={() => void copyTgCode()}>
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[12px] text-ink-3">Если по кнопке не привязалось — откройте бота и отправьте этот код сообщением.</p>
                  </div>
                )}
                <button className="btn-ghost !justify-center text-ink-3" onClick={() => setTgWaiting(false)}>
                  Отменить ожидание
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink-3">Привяжите Telegram, чтобы получать личные уведомления.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button className="btn-primary !justify-center" onClick={() => void linkTelegram()} disabled={tgBusy}>
                    {tgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Привязать Telegram
                  </button>
                  <button className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1 px-1 h-7" onClick={() => void refreshTg()} disabled={tgBusy} title="Запасной вариант: проверить статус вручную, если авто-обновление не сработало">
                    {tgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Проверить
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Только супер-админ: временно снять суточный замок карточек для всех сотрудников —
            на случай, когда нужно массово дозагрузить информацию в старые игры. */}
        {isSuperAdmin && (
          <div className="panel p-5 sm:p-6">
            <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-2">
              {coreUnlockOn ? <Unlock className="w-4 h-4 text-status-refused" /> : <Lock className="w-4 h-4 text-accent" />}
              Правка старых карточек
            </h2>
            {coreUnlockOn ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2.5 rounded-xl px-3.5 py-3 bg-status-refused/10 border border-status-refused/25">
                  <AlertTriangle className="w-4 h-4 text-status-refused flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 text-sm">
                    <div className="font-semibold text-status-refused">Сейчас разрешено всем</div>
                    <div className="text-[12px] text-ink-3 mt-0.5">
                      Суточный (и архивный) замок карточек временно снят — любой сотрудник может
                      поменять дату/бригаду/сумму/статус в старой игре, не только в первые сутки.
                      {coreUnlock?.since ? ` Включено: ${new Date(coreUnlock.since).toLocaleString('ru-RU')}${coreUnlock.by ? ` (${coreUnlock.by})` : ''}.` : ''}
                    </div>
                  </div>
                </div>
                {coreUnlockConfirm ? (
                  <div className="flex gap-2">
                    <button className="btn-primary flex-1 !justify-center" onClick={() => void toggleCoreUnlock(false)} disabled={coreUnlockBusy}>
                      {coreUnlockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Да, вернуть замок
                    </button>
                    <button className="btn-soft !justify-center" onClick={() => setCoreUnlockConfirm(false)} disabled={coreUnlockBusy}>Отмена</button>
                  </div>
                ) : (
                  <button className="btn-soft !justify-center w-full text-status-refused" onClick={() => setCoreUnlockConfirm(true)}>
                    <Lock className="w-4 h-4" /> Вернуть суточный замок
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink-3">
                  Обычно карточку игры нельзя трогать спустя сутки после игры (кассе — трое суток,
                  только сумму). Эта кнопка временно снимает замок для всех — пока вы сами не
                  вернёте его обратно.
                </p>
                {coreUnlockConfirm ? (
                  <div className="rounded-xl border border-status-refused/30 bg-status-refused/5 p-3.5 space-y-3">
                    <div className="text-sm text-ink-1 font-medium">Точно разрешить правку всем старым карточкам?</div>
                    <div className="flex gap-2">
                      <button className="btn-danger flex-1 !justify-center" onClick={() => void toggleCoreUnlock(true)} disabled={coreUnlockBusy}>
                        {coreUnlockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Да, разрешить
                      </button>
                      <button className="btn-soft !justify-center" onClick={() => setCoreUnlockConfirm(false)} disabled={coreUnlockBusy}>Отмена</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-soft !justify-center w-full" onClick={() => setCoreUnlockConfirm(true)}>
                    <Unlock className="w-4 h-4" /> Разрешить правку старых карточек
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="panel p-5 sm:p-6 space-y-2">
          <h2 className="text-sm font-semibold text-ink-2 mb-3">Настройки</h2>
          <button className="w-full flex items-center justify-between h-12 px-4 rounded-xl hover:bg-surface-2 transition-colors"
            onClick={() => patchSettings({ theme: isDark ? 'light' : 'dark' })}>
            <span className="flex items-center gap-3 text-ink-1">
              {isDark ? <Moon className="w-4 h-4 text-accent" /> : <Sun className="w-4 h-4 text-accent" />} Тема оформления
            </span>
            <span className="text-sm text-ink-3">{isDark ? 'Тёмная' : 'Светлая'}</span>
          </button>

          {/* Смена пароля — только для входа по логину; у Битрикс-входа пароля нет.
              Служебной учётке супер-админа блок смены пароля не показываем вовсе. */}
          {isSuperAdmin ? null : isLocal ? (
            pwOpen ? (
              <div className="rounded-xl border border-line p-4 space-y-3">
                <div className="flex items-center gap-2 text-ink-1 font-medium text-sm"><KeyRound className="w-4 h-4 text-accent" /> Смена пароля</div>
                <input className="input" type="password" placeholder="Текущий пароль" autoComplete="current-password" value={curPw} onChange={(e) => setCurPw(e.target.value)} disabled={pwBusy} />
                <input className="input" type="password" placeholder="Новый пароль (мин. 6 символов)" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} disabled={pwBusy} />
                <input className="input" type="password" placeholder="Повторите новый пароль" autoComplete="new-password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} disabled={pwBusy} />
                <div className="flex gap-2">
                  <button className="btn-primary flex-1 !justify-center" onClick={() => void submitPassword()} disabled={pwBusy}>
                    {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Сохранить
                  </button>
                  <button className="btn-soft !justify-center" onClick={() => { setPwOpen(false); setCurPw(''); setNewPw(''); setNewPw2(''); }} disabled={pwBusy}>Отмена</button>
                </div>
              </div>
            ) : (
              <button className="w-full flex items-center gap-3 h-12 px-4 rounded-xl text-ink-1 hover:bg-surface-2 transition-colors"
                onClick={() => setPwOpen(true)}>
                <KeyRound className="w-4 h-4 text-ink-2" /> Сменить пароль
              </button>
            )
          ) : (
            <div className="w-full flex items-center gap-3 min-h-12 px-4 py-2 rounded-xl text-ink-3 text-sm">
              <KeyRound className="w-4 h-4 flex-shrink-0" /> Вы входите через Битрикс24 — пароль не используется.
            </div>
          )}

          {confirmExit ? (
            <button className="w-full flex items-center gap-3 h-12 px-4 rounded-xl text-white bg-status-refused hover:brightness-105 font-semibold transition-colors"
              onClick={() => void logout()}>
              <LogOut className="w-4 h-4" /> Точно выйти?
            </button>
          ) : (
            <button className="w-full flex items-center gap-3 h-12 px-4 rounded-xl text-status-refused hover:bg-status-refused/10 transition-colors"
              onClick={() => setConfirmExit(true)}>
              <LogOut className="w-4 h-4" /> Выйти
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 'YYYY-MM-DD' → 'ДД.ММ.ГГГГ' для отображения (ввод — обычный <input type="date">). */
function ddmmyyyy(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function InfoRow({ icon: Icon, label, value, full }: { icon: typeof Mail; label: string; value?: string; full?: boolean }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-xs text-ink-3 mb-1 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</dt>
      <dd className="text-ink-1 break-words">{value || <span className="text-ink-3">—</span>}</dd>
    </div>
  );
}
